import { Card, CardContent } from '@/components/ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { cn } from '@/lib/utils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

type MainPageCarouselProps = {
  onConnectCalendar: () => void;
  isGoogleCalendarEnabled: boolean;
};

export function MainPageCarousel({ onConnectCalendar, isGoogleCalendarEnabled }: MainPageCarouselProps) {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const { t } = useTranslation();

  const slides = [
    ...(isGoogleCalendarEnabled
      ? [
          {
            title: t('homepage.carousel.connectCalendarTitle'),
            description: (
              <>
                {t('homepage.carousel.connectCalendarDescription')}{' '}
                <button
                  onClick={onConnectCalendar}
                  className="text-indigo-600 hover:underline transition-colors duration-200 font-medium"
                >
                  {t('homepage.carousel.connectCalendarCta')}
                </button>
              </>
            ),
            image: '/images/homepage/calendar-integration.jpg',
          },
        ]
      : []),
    {
      title: t('homepage.carousel.startMeetingTitle'),
      description: t('homepage.carousel.startMeetingDescription'),
      image: '/images/homepage/start-meeting.jpg',
    },
    {
      title: t('homepage.carousel.aiTitle'),
      description: t('homepage.carousel.aiDescription'),
      image: '/images/homepage/ai-assistant.jpg',
    },
    {
      title: t('homepage.carousel.tryPremiumTitle'),
      description: (
        <>
          {t('homepage.carousel.tryPremiumDescription')}{' '}
          <Link to="/pricing" className="text-indigo-600 hover:underline transition-colors duration-200 font-medium">
            {t('homepage.carousel.tryPremiumCta')}
          </Link>
        </>
      ),
      image: '/images/homepage/premium-features.jpg',
    },
  ];

  return (
    <div className="w-full max-w-64 mx-auto">
      <Carousel
        opts={{ loop: true }}
        className="relative w-full"
        setApi={(api) => {
          if (!api) return;
          api.on('select', () => setSelectedIndex(api.selectedScrollSnap()));
        }}
      >
        <CarouselContent>
          {slides.map((slide, index) => (
            <CarouselItem key={index}>
              <Card className="flex flex-col items-center justify-center text-center gap-4 shadow-none border-none">
                <img src={slide.image} alt={slide.title} className="w-48 h-48 object-cover" />
                <CardContent>
                  <h3 className="text-lg font-semibold mb-1">{slide.title}</h3>
                  <p className="text-sm text-muted-foreground">{slide.description}</p>
                </CardContent>
              </Card>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious />
        <CarouselNext />
        {/* Dot indicators */}
        <div className="flex justify-center gap-2">
          {slides.map((_, i) => (
            <div
              key={i}
              className={cn(
                'w-2 h-2 rounded-full transition-colors',
                i === selectedIndex ? 'bg-indigo-500' : 'bg-muted'
              )}
            />
          ))}
        </div>
      </Carousel>
    </div>
  );
}
